import { Button, Text, Flex, Box } from '@chakra-ui/react';
import { useBreakpoint } from 'hooks/utils/layout/useBreakpoint';
import React, { ReactElement } from 'react';
import { Breakpoints } from 'types/breakpoints';
import { ModalWorksheetShareVariantProps, SharedModalProps } from 'types/Modal';
import { EmailIcon } from 'assets/icons/v2';
import { NovaIcon } from 'components/ui/novaIcon';
import {
  useGeneratePublicLink,
  useInviteUserToWorksheet,
  useGetWorksheetShareLink,
} from 'hooks/api/backend/worksheets/useWorksheets';
import Joi from 'joi';
import { errorMessage } from 'components/common/Form/ErrorMessages';
import { Separator } from 'components/common/Separator';
import { useNovaToast } from 'hooks/utils/useNovaToast';
import { ToastVariant } from 'types/Toast';
import { CopyToClipboard } from 'components/common/CopyToClipboard';
import { Form, RHFFormInput } from 'components/common/Form/v2';
import { CustomModal } from '../../CustomModal';
import { CustomModalContent } from '../../CustomModalContent';

type Props = SharedModalProps & ModalWorksheetShareVariantProps;

type InviteUserEmail = {
  email: string;
};

const emailSchema = Joi.object({
  email: Joi.string()
    .email({
      tlds: {
        allow: false,
      },
    })
    .required()
    .messages({
      'string.empty': errorMessage.empty,
      'string.email': errorMessage.email,
    }),
});

export const WorksheetShareModal = ({ handleClose, isShown, worksheetId }: Props): ReactElement => {
  const isDesktop = useBreakpoint(Breakpoints.DESKTOP);
  const { mutateAsync: inviteUser, isPending } = useInviteUserToWorksheet(worksheetId);
  const {
    data: existingShareLink,
    isLoading: isLoadingShareLink,
    refetch: refetchShareLink,
  } = useGetWorksheetShareLink(worksheetId);
  const { mutateAsync: generateLink, isPending: isGeneratingLink } =
    useGeneratePublicLink(worksheetId);
  const { show: showToast } = useNovaToast();

  const handleGenerateLink = async (): Promise<void> => {
    try {
      await generateLink();
      await refetchShareLink();
      showToast({
        title: 'Öffentlicher Link erstellt!',
        variant: ToastVariant.SUCCESS,
      });
    } catch (error) {
      showToast({
        title: 'Fehler beim Erstellen des Links',
        variant: ToastVariant.ERROR,
      });
    }
  };

  const handleSubmit = async (values: InviteUserEmail): Promise<void> => {
    try {
      await inviteUser(values.email);
      showToast({
        title: 'Einladung gesendet!',
        variant: ToastVariant.SUCCESS,
      });
      handleClose();
    } catch (error) {
      showToast({
        title: 'Fehler beim Senden der Einladung',
        variant: ToastVariant.ERROR,
      });
    }
  };

  return (
    <CustomModal
      isOpen={isShown}
      onClose={handleClose}
      placement="center"
      size="lg"
      hideHeader={!isDesktop}
    >
      <CustomModalContent
        p={{ base: '0', md: '0 40px 30px' }}
        display="flex"
        flexDirection="column"
      >
        <Flex
          p={{ base: 'l', md: '0' }}
          textAlign={{ base: 'center', md: 'left' }}
          mb={{ md: '30px' }}
          flexDir="column"
          gap="l"
        >
          <Text textStyle="h3.medium" color="text.primary">
            Arbeitsblatt teilen
          </Text>
          <Text>
            Lade eine Lehrperson ein, auf dieses Material zuzugreifen. Sie kann es ansehen, kopieren
            und in ihrer Arbeit verwenden.
          </Text>
          <Form<InviteUserEmail, typeof emailSchema>
            onSubmit={handleSubmit}
            schema={emailSchema}
            autoComplete="on"
            options={{
              reValidateMode: 'onChange',
            }}
          >
            <Flex gap="l" width="100%" justifyContent="right">
              <RHFFormInput
                type="email"
                leftElement={
                  <NovaIcon icon={EmailIcon} size="m" color="icon.quaternary" aria-hidden />
                }
                placeholder="Email"
                flexGrow={1}
                textTransform="lowercase"
                name="email"
              />

              <Button type="submit" variant="primary" flexShrink={0} loading={isPending}>
                Einladung senden
              </Button>
            </Flex>
          </Form>
        </Flex>
        <Box width="100%" mb="l">
          <Separator text="Oder" color="divider.default" lineColor="border.accent" />
        </Box>
        {!isLoadingShareLink && existingShareLink?.shareLink ? (
          <CopyToClipboard text={existingShareLink.shareLink} />
        ) : (
          <Flex justifyContent="center">
            <Button onClick={handleGenerateLink} loading={isGeneratingLink} variant="primary">
              Öffentlichen Link erstellen
            </Button>
          </Flex>
        )}
      </CustomModalContent>
    </CustomModal>
  );
};
